import cn from 'classnames';
import { useRouter } from 'next/navigation';
import React, { forwardRef } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from 'src/components/Button';
import { Checkbox } from 'src/components/FormNew/Checkbox';
import { Input } from 'src/components/FormNew/Input';
import { Submit } from 'src/components/FormNew/Submit';
import { TextArea } from 'src/components/FormNew/TextArea';
import { UserAgreementCheckboxes } from 'src/components/FormNew/UserAgreementCheckboxes';
import { Link } from 'src/components/Link';
import { ModalWrapper } from 'src/components/Modals/ModalWrapper';
import { Wrapper } from 'src/components/Wrapper';
import {
  AnalyticsFormType,
  FillFormFieldBase,
  SuccessfullSubmissionFormAnalyticsType,
  TryFormProps,
  useAnalytics,
} from 'src/hooks/useAnalytics';
import { useGtmDataLayer } from 'src/hooks/useGtmDataLayer';
import { useSubmitForm } from 'src/hooks/useSubmitForm';
import { useTryFormErrorsCheck } from 'src/hooks/useTryFormErrorsCheck';
import { DataFromFormInputs, FieldsNamesType, FormStatus } from 'src/types';

import s from './NewMainForm.module.scss';

interface DefaultNoticeProps {
  title: string;
  buttonText?: string;
  linkHref?: string;
  isError?: boolean;
  image?: string;
  text?: string;
  TextNode?: React.ReactNode;
  wrapped?: boolean;
  analyticsButtonId: string;
  buttons?: {
    title: string;
    href: string;
    onClick?: () => void | undefined;
  }[];
}

function DefaultNotice({
  title,
  analyticsButtonId,
  buttonText = '',
  linkHref,
  wrapped,
  isError = false,
  image,
  text,
  TextNode,
  buttons,
}: DefaultNoticeProps) {
  const router = useRouter();

  return (
    <div className={cn(s.noticeBlock, { [s.noticeWrapped]: wrapped })}>
      <div className={s.noticeWrapper}>
        <h2
          className={s.noticeTitle}
          dangerouslySetInnerHTML={{ __html: title }}
        />
        {text && (
          <p
            className={s.noticeText}
            dangerouslySetInnerHTML={{ __html: text }}
          />
        )}
        {TextNode && TextNode}
        {image && (
          <img
            src={image}
            width={240}
            height={240}
            alt="notice image"
            className={s.noticeImage}
          />
        )}

        {buttonText && linkHref && (
          <Link className={s.noticeLink} href={linkHref} withButton>
            <Button
              fontSize="large"
              variant="primary"
              data-qa="notice-button"
              className={s.noticeButton}
              id={`main-form-notice-modal-link-${analyticsButtonId}`}
              analyticsOptions={{
                action: 'click',
                clickZone: 'body',
                clickElement: 'button',
                clickContent: buttonText,
                uniqueId: `main-form-notice-modal-link-${analyticsButtonId}`,
                transitionType: 'inside-link',
              }}
            >
              {buttonText}
            </Button>
          </Link>
        )}

        {buttonText && (
          <Button
            onClick={() => (!isError ? router.push('/') : router.refresh())}
            fontSize="large"
            variant="primary"
            data-qa="notice-button"
            className={s.noticeButton}
            id={`main-form-notice-modal-button-${analyticsButtonId}`}
            analyticsOptions={{
              action: 'click',
              clickZone: 'body',
              clickElement: 'button',
              clickContent: buttonText,
              uniqueId: `main-form-notice-modal-button-${analyticsButtonId}`,
              transitionType: 'inside-link',
            }}
          >
            {buttonText}
          </Button>
        )}

        {buttons &&
          buttons.length > 0 &&
          buttons.map((button, index) => {
            return (
              <Link
                key={index}
                className={s.noticeLink}
                href={button.href}
                withButton
                target="_blank"
              >
                <Button
                  fontSize="large"
                  variant="primary"
                  data-qa="notice-button"
                  className={s.noticeButton}
                  id={`main-form-notice-modal-link-${analyticsButtonId}`}
                  analyticsOptions={{
                    action: 'click',
                    clickZone: 'body',
                    clickElement: 'button',
                    clickContent: buttonText,
                    uniqueId: `main-form-notice-modal-link-${analyticsButtonId}`,
                    transitionType: 'inside-link',
                  }}
                >
                  {button.title}
                </Button>
              </Link>
            );
          })}
      </div>
    </div>
  );
}

function ModalNotice({
  isError = false,
  notice,
  analyticsButtonId,
  setFormStatus,
}: {
  isError: boolean;
  analyticsButtonId: string;
  notice: NoticeType;
  setFormStatus: React.Dispatch<React.SetStateAction<FormStatus>>;
}) {
  return (
    <div className={s.notification}>
      <p
        className={cn(s.notificationText, {
          [s.notificationTextError]: isError,
        })}
      >
        {notice.title}
      </p>
      {isError && (
        <Button
          className={s.notificationButton}
          data-qa="retry-button"
          onClick={() => setFormStatus('idle')}
          id={`main-form-modal-retry-button-${analyticsButtonId}`}
          analyticsOptions={{
            action: 'click',
            clickZone: 'body',
            clickElement: 'button',
            clickContent: 'Попробовать еще раз',
            uniqueId: `main-form-modal-retry-button-${analyticsButtonId}`,
            transitionType: 'inside-link',
          }}
        >
          Попробовать еще раз
        </Button>
      )}
    </div>
  );
}

type NoticeType = {
  title: string;
  text?: string;
  buttonTitle?: string;
  linkHref?: string;
  image?: string;
  TextNode?: React.ReactNode;
  buttons?: {
    title: string;
    href: string;
    onClick?: () => void | undefined;
  }[];
};

type CustomNoticeBlockProps = React.FC<{
  title?: string;
  text?: string;
  setFormStatus: React.Dispatch<React.SetStateAction<FormStatus>>;
}>;

interface NoticeBlockProps {
  formStatus: FormStatus;
  CustomNoticeBlock?: CustomNoticeBlockProps;
  successNotice: NoticeType;
  errorNotice: NoticeType;
  setFormStatus: React.Dispatch<React.SetStateAction<FormStatus>>;
  wrapped?: boolean;
  analyticsButtonId: string;
  formVariant: 'inner' | 'modal';
}

function NoticeBlock({
  formStatus,
  analyticsButtonId,
  CustomNoticeBlock,
  successNotice,
  errorNotice,
  setFormStatus,
  wrapped,
  formVariant,
}: NoticeBlockProps) {
  const notice = formStatus === 'success' ? successNotice : errorNotice;

  if (formStatus !== 'success' && formStatus !== 'error') return null;

  return (
    <>
      {CustomNoticeBlock && (
        <CustomNoticeBlock
          title={notice?.title}
          text={notice?.text}
          setFormStatus={setFormStatus}
        />
      )}
      {formVariant === 'modal' && (
        <ModalNotice
          notice={notice}
          isError={formStatus === 'error'}
          setFormStatus={setFormStatus}
          analyticsButtonId={analyticsButtonId}
        />
      )}
      {!CustomNoticeBlock && formVariant !== 'modal' && (
        <DefaultNotice
          title={notice?.title}
          wrapped={wrapped}
          buttonText={notice?.buttonTitle}
          linkHref={notice?.linkHref}
          TextNode={notice?.TextNode}
          analyticsButtonId={analyticsButtonId}
          text={notice?.text}
          image={notice?.image}
          buttons={notice?.buttons}
        />
      )}
    </>
  );
}

function FormHeader({
  title,
  subtitle,
}: {
  title?: string;
  subtitle?: string;
}) {
  return (
    <div className={s.header}>
      {title && <h2 className={s.title}>{title}</h2>}
      {subtitle && <h3 className={s.subtitle}>{subtitle}</h3>}
    </div>
  );
}

interface FormBaseProps {
  allFormsSentFormType: AnalyticsFormType;
  allFormsSentIsPopup?: boolean;
  consultationFormType?: 'header' | 'body';
  saleFormType?: {
    type: 'sale' | 'consultation' | 'calculator' | 'promo';
    platformName:
      | 'Облако VMware'
      | 'Advanced'
      | 'Evolution'
      | 'ML Space'
      | 'combo'
      | 'none';
  };
  modalClose?: () => void;
  platformSlug?: string;
  productSlug?: string;
  title?: string;
  formVariant: 'modal' | 'inner';
  id?: string;
  small?: boolean;
  theme?: 'base' | 'purple';
  subtitle?: string;
  productTitle?: string;
  eventCategory?: string;
  withoutMargin?: boolean;
  marketingEventIDs?: string[];
  withPositionInput?: boolean;
  successNotice?: Partial<NoticeType>;
  CustomNoticeBlock?: CustomNoticeBlockProps;
  errorNotice?: Partial<NoticeType>;
  inputPath: string;
  fieldsNames?: FieldsNamesType;
  analyticsFieldFill: Omit<FillFormFieldBase, 'fieldName'>;
  buttonTitle?: string;
  short?: boolean;
  wrapped?: boolean;
  tryFormType: TryFormProps['formType'];
  dataLayer?: {
    event?: string;
    event_category?: string;
    event_action?: string;
    event_label: string;
  };
  additionalFields?: {
    leadSource?: string;
    subsegment?: string;
    priority?: string;
  };
  agreementName?: 'site_services_consent' | 'online_event_consent';
  successfulSubmissonFormAnalytics?: SuccessfullSubmissionFormAnalyticsType;
}

function FormBase({
  id = '',
  successNotice = {
    title: 'Мы приняли вашу заявку, скоро с вами свяжется специалист.',
  },
  errorNotice = {
    buttonTitle: 'Обновить страницу',
  },
  withoutMargin = false,
  theme = 'base',
  buttonTitle = 'Отправить',
  short = false,
  small = false,
  wrapped = false,
  title,
  subtitle,
  fieldsNames,
  analyticsFieldFill,
  CustomNoticeBlock,
  productSlug,
  platformSlug,
  marketingEventIDs,
  tryFormType,
  inputPath,
  dataLayer,
  formVariant,
  withPositionInput,
  allFormsSentFormType,
  allFormsSentIsPopup,
  consultationFormType,
  saleFormType,
  additionalFields,
  agreementName,
  successfulSubmissonFormAnalytics,
}: FormBaseProps) {
  const dataFields = {
    fields: 'ba68da97a4162765cbb785d623b81814d0d1d00c4b61560f923090d1c83f56aa',
    leadSource:
      'b4137ddb2a845a933ebf6674d9afebc28d6bb532d15e25a26c2135130f378c7d',
    subsegment:
      'cf2cb923cc6a1ba8c56db485216355f94ff3aa4fff2880951382bdaf834e03a6',
    priority:
      '3092a5f0dba05ff3cadc3c2c8eeacd47940b1837f9534bc559d19d8435c8b219',
  };

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitting },
    reset,
    watch,
  } = useForm<DataFromFormInputs>({
    mode: 'onChange',
    defaultValues: {
      userAgreement: true,
    },
  });

  const {
    fillFormField,
    tryFormAnalytics,
    consultationFormSent,
    saleFormSent,
  } = useAnalytics();
  const isPhisic = !!watch('isNaturalPerson', false);
  const { sendDataToDataLayer } = useGtmDataLayer();
  const { submitForm, formStatus, setFormStatus, resultCode, formMessage } =
    useSubmitForm();
  const { successfullSubmissionFormAnalytics } = useAnalytics();

  useTryFormErrorsCheck(errors, isSubmitting, tryFormType);

  const sendFormFillAnalytics = (fieldName: string) => {
    const { formType, popupPlace, buttonId } = analyticsFieldFill;
    if (formType === 'body') {
      fillFormField({
        formType: 'body',
        fieldName: fieldName,
      });
    }
    if (popupPlace && buttonId) {
      fillFormField({
        formType: 'popup',
        fieldName: fieldName,
        popupPlace: popupPlace,
        buttonId: buttonId,
      });
    }

    return null;
  };

  const onSubmit = async (data: DataFromFormInputs) => {
    data = {
      ...data,
      marketingEventIDs,
      referer: document.referrer,
      isNaturalPerson: fieldsNames?.checkbox?.isHidden
        ? false
        : data.isNaturalPerson,
    };

    if (productSlug) {
      data.productSlug = productSlug;
    }

    if (platformSlug) {
      data.platformSlug = platformSlug;
    }

    if (additionalFields) {
      const elmaFields: { [key: string]: string } = {};

      if (additionalFields.leadSource) {
        elmaFields[dataFields.leadSource] = additionalFields.leadSource;
      }

      if (additionalFields.priority) {
        elmaFields[dataFields.priority] = additionalFields.priority;
      }

      if (additionalFields.subsegment) {
        elmaFields[dataFields.subsegment] = additionalFields.subsegment;
      }

      data[dataFields.fields] = elmaFields;
    }

    if (data) {
      const other_consents = data.site_services_consent
        ? ['site_services_consent']
        : ['online_event_consent'];

      if (data.site_services_consent) {
        delete data.site_services_consent;
      } else {
        delete data.online_event_consent;
      }

      await submitForm({
        backendPatchInput: `/forms/${inputPath}`,
        allFormSentFormType: allFormsSentFormType,
        allFormSentIsPopup: allFormsSentIsPopup,
        dataFromFormInputs: {
          ...data,
          other_consents,
        },
        onSuccess: () => {
          tryFormAnalytics({ formType: tryFormType, action: 'Отправлено' });
          if (consultationFormType) {
            consultationFormSent(
              consultationFormType,
              !!data.subscribeForMailing,
            );
          }
          if (saleFormType)
            saleFormSent(
              saleFormType.type,
              !!data.subscribeForMailing,
              saleFormType.platformName, //FIXME: заменить нейминг на platformSlug
            );
          sendDataToDataLayer({
            dataLayer,
          });
          successfulSubmissonFormAnalytics &&
            successfullSubmissionFormAnalytics(
              successfulSubmissonFormAnalytics,
            );
          reset();
        },
        onError: () => {
          tryFormAnalytics({
            formType: tryFormType,
            action: 'Ошибка',
            errorCode: resultCode,
          });
        },
      });
    }
  };

  const formErrorNotice = {
    ...errorNotice,
    title: errorNotice?.title ?? formMessage,
  };

  const formSuccessNotice = {
    ...successNotice,
    title: successNotice?.title ?? formMessage,
  };

  const analyticsButtonId =
    analyticsFieldFill.formType === 'popup'
      ? `modal-${analyticsFieldFill.popupPlace}`
      : analyticsFieldFill.formType;

  return (
    <div id={id}>
      <div
        className={cn(s.formWrapper, {
          [s.short]: short,
          [s.small]: small,
          [s.modal]: formVariant === 'modal',
          [s.wrapped]: wrapped,
          [s.purple]: theme === 'purple',
          [s.hidden]: formStatus !== 'idle' && formStatus !== 'sending',
          [s.withoutMargin]: withoutMargin,
        })}
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          data-qa="section_form"
          className={s.form}
        >
          <FormHeader title={title} subtitle={subtitle} />
          <div className={s.inputGrid}>
            <Input
              label={fieldsNames?.name?.label ?? 'Имя*'}
              placeholder={fieldsNames?.name?.placeholder ?? 'Введите ваше имя'}
              name="name"
              validationType="name"
              theme={theme}
              errors={errors}
              register={register}
              required
              data-qa="section_input"
              onBlurCustom={(event) => {
                if (event.currentTarget.value) {
                  sendFormFillAnalytics('name');
                }
              }}
            />
            <Input
              label={fieldsNames?.phone?.label ?? 'Телефон*'}
              placeholder={
                fieldsNames?.phone?.placeholder ?? 'Введите ваш телефон'
              }
              name="phone"
              theme={theme}
              inputType="phone"
              validationType="phone"
              errors={errors}
              register={register}
              required
              data-qa="section_input"
              onBlurCustom={(event) => {
                if (event.currentTarget.value) {
                  sendFormFillAnalytics('tel');
                }
              }}
            />
          </div>
          {!fieldsNames?.email?.isHidden && (
            <Input
              label={fieldsNames?.email?.label ?? 'Электронная почта*'}
              placeholder={
                fieldsNames?.email?.placeholder ?? 'Введите корректный email'
              }
              theme={theme}
              name="email"
              validationType="email"
              errors={errors}
              register={register}
              required={fieldsNames?.email?.required ?? true}
              data-qa="section_input"
              onBlurCustom={(event) => {
                if (event.currentTarget.value) {
                  sendFormFillAnalytics('email');
                }
              }}
            />
          )}
          {!short && (
            <>
              {!fieldsNames?.checkbox?.isHidden && (
                <Checkbox
                  className={s.checkbox}
                  label={fieldsNames?.checkbox?.text ?? 'Я физлицо'}
                  name="isNaturalPerson"
                  variant={theme === 'purple' ? 'purple' : 'dark'}
                  errors={errors}
                  register={register}
                  data-qa="section_checkbox"
                  required={false}
                  onCustomChange={(event) => {
                    if (event.currentTarget.value) {
                      sendFormFillAnalytics('checkbox fiz');
                    }
                  }}
                />
              )}

              <div className={cn({ [s.inputsGroup]: withPositionInput })}>
                <Input
                  label={fieldsNames?.company?.label ?? 'Название компании*'}
                  placeholder={
                    fieldsNames?.company?.placeholder ?? 'Укажите компанию'
                  }
                  name="company"
                  validationType="company"
                  theme={theme}
                  errors={errors}
                  register={register}
                  disabled={isPhisic}
                  data-qa="section_input"
                  required
                  onBlurCustom={(event) => {
                    if (event.currentTarget.value) {
                      sendFormFillAnalytics('company');
                    }
                  }}
                />
                {withPositionInput && (
                  <Input
                    label={fieldsNames?.position?.label ?? 'Должность*'}
                    placeholder={
                      fieldsNames?.position?.placeholder ?? 'Введите должность'
                    }
                    name="position"
                    validationType="position"
                    errors={errors}
                    theme={theme}
                    register={register}
                    disabled={isPhisic}
                    data-qa="section_input"
                    required
                    onBlurCustom={(event) => {
                      if (event.currentTarget.value) {
                        sendFormFillAnalytics('position');
                      }
                    }}
                  />
                )}
              </div>

              <TextArea
                label={fieldsNames?.comment?.label ?? 'Ваш запрос'}
                placeholder={
                  fieldsNames?.comment?.placeholder ?? 'Напишите ваш запрос'
                }
                name="comment"
                errors={errors}
                theme={theme}
                register={register}
                minHeight="60"
                required={false}
                textareaClassName={s.textArea}
                onBlurCustom={(event) => {
                  if (event.currentTarget.value) {
                    sendFormFillAnalytics('comment');
                  }
                }}
              />
            </>
          )}

          <UserAgreementCheckboxes
            checkboxClassName={s.checkbox}
            agreementName={agreementName}
            errors={errors}
            fieldsNames={{
              userAgreement: fieldsNames?.userAgreement,
              checkboxSubscribe: fieldsNames?.checkboxSubscribe,
            }}
            register={register}
            sendFormFillAnalytics={sendFormFillAnalytics}
            theme={theme === 'purple' ? 'purple' : 'dark'}
          />

          <Submit disabled={formStatus === 'sending'} className={s.submit}>
            <Button
              className={s.button}
              data-qa="section_btn"
              loading={formStatus === 'sending'}
              id={`main-form-button-${analyticsButtonId}`}
              analyticsOptions={{
                action: 'click',
                clickZone: 'body',
                clickElement: 'button',
                clickContent: buttonTitle,
                uniqueId: `main-form-button-${analyticsButtonId}`,
                transitionType: 'inside-link',
              }}
              onClick={() => trigger()}
            >
              {buttonTitle}
            </Button>
          </Submit>
        </form>
      </div>
      <NoticeBlock
        formStatus={formStatus}
        CustomNoticeBlock={CustomNoticeBlock}
        successNotice={formSuccessNotice}
        analyticsButtonId={analyticsButtonId}
        errorNotice={formErrorNotice}
        setFormStatus={setFormStatus}
        wrapped={wrapped}
        formVariant={formVariant}
      />
    </div>
  );
}

function PageForm({ ...props }: FormBaseProps) {
  return (
    <FormBase
      {...props}
      successfulSubmissonFormAnalytics={
        props.successfulSubmissonFormAnalytics && {
          zoneclick: props.successfulSubmissonFormAnalytics.zoneclick || 'B',
          formname: props.successfulSubmissonFormAnalytics?.formname,
        }
      }
    />
  );
}

function ModalForm({
  modalClose,
  productTitle,
  eventCategory,
  ...props
}: FormBaseProps) {
  return (
    <ModalWrapper
      closeClassName={s.close}
      className={s.modalRoot}
      portalClassName={s.portal}
      onClickForClose={() => {
        modalClose && modalClose();
      }}
    >
      <FormBase
        small
        withPositionInput
        dataLayer={{
          event: 'custom_event',
          event_category: eventCategory || 'consultation LeadForm',
          event_action: 'formSuccess',
          event_label: productTitle ? productTitle : 'mainPage',
        }}
        fieldsNames={{
          comment: { label: 'Комментарий', placeholder: 'Введите комментарий' },
          checkbox: { text: 'Физлицо' },
        }}
        {...props}
        successfulSubmissonFormAnalytics={
          props.successfulSubmissonFormAnalytics && {
            zoneclick: props.successfulSubmissonFormAnalytics.zoneclick || 'P',
            formname: props.successfulSubmissonFormAnalytics?.formname,
          }
        }
        allFormsSentIsPopup
      />
    </ModalWrapper>
  );
}

interface FormWrapperProps {
  wrapped?: boolean;
  children: React.ReactNode;
}

const FormWrapper = forwardRef<HTMLDivElement, FormWrapperProps>(
  ({ wrapped, children }, ref) => {
    if (wrapped)
      return (
        <Wrapper className={s.wrapper} ref={ref}>
          {children}
        </Wrapper>
      );

    return <div ref={ref}>{children}</div>;
  },
);

FormWrapper.displayName = 'FormWrapper';

// Варианты формы:
// modal - для модальных окон
// inner - для встроенных в страницу

// Конфигурация формы:
// short - короткая форма
// small - уменьшены отступы и размер заголовка
// wrapped - с враппером

export const NewMainForm = forwardRef<HTMLDivElement, FormBaseProps>(
  ({ wrapped, formVariant, ...props }, ref) => {
    return (
      <FormWrapper wrapped={wrapped} ref={ref}>
        {formVariant === 'modal' && (
          <ModalForm formVariant={formVariant} {...props} />
        )}
        {formVariant === 'inner' && (
          <PageForm formVariant={formVariant} wrapped={wrapped} {...props} />
        )}
      </FormWrapper>
    );
  },
);

NewMainForm.displayName = 'NewMainForm';
